// src/frontend/js/modern/system.js
import { eventBus, EVENTS } from './core/event-bus.js';
import { queueManager } from './core/queue.js';
import { eventManager } from './core/event-manager.js';
import { syncManager } from './core/sync.js';
import uiManager from './core/ui-manager.js';
import thirdPartyIntegration from './core/third-party-integration.js';
import toastr from './lib/toastr-lite.js';
import { conflictManager } from './core/conflict-manager.js';
import { setInitGate } from './core/init-gate.js';
import { resilientStorage } from './core/storage/resilient-storage.js';
import { displayStore } from './core/storage/display-store.js';
import { showInfo } from './shared/ui-helpers.js';
import { validators } from './shared/validators.js';
import {
	initPersistenceWriter,
	recoverFromPersistence,
} from './core/persistence-writer.js';
import accountGuard from "./core/account-guard";
import stateCache from "./core/state-cache";

// Popup API imports for the legacy compatibility bridge
import {
	showPopup,
	hidePopup,
	getOrCreate,
	hideAllPopups,
	isPopupVisible,
	destroyPopup
} from './lib/popup-lite.js';

// ═══════════════════════════════════════════════════════════
// MODERN GLOBALS
// ═══════════════════════════════════════════════════════════

// Modern toastr instance (used internally by ui-helpers)
window.wlfmc_toastr = toastr;

// Modern popup API (structured, namespaced)
window.wlfmc_popup = Object.freeze({
	show: showPopup,
	hide: hidePopup,
	getOrCreate: getOrCreate,
	hideAll: hideAllPopups,
	isVisible: isPopupVisible,
	destroy: destroyPopup,
});

// ═══════════════════════════════════════════════════════════
// LEGACY COMPATIBILITY BRIDGE
//
// External plugins (e.g. AFS - Ask For Estimate) depend on
// legacy globals that only exist when the OLD script bundle
// is loaded. When the modern bundle replaces the old one,
// these globals disappear and the external plugin breaks.
//
// This bridge recreates those globals on top of the modern
// implementations so external plugins work WITHOUT any
// code changes, regardless of which mode is active.
//
// Guards (if checks) ensure that if the legacy script IS
// also loaded (e.g. during migration), we never overwrite
// its definitions.
// ═══════════════════════════════════════════════════════════

// 1) Global `toastr` alias
//    Legacy code calls: toastr.error('...') / toastr.success('...')
//    Modern equivalent: window.wlfmc_toastr
window.toastr = window.toastr || toastr;

/**
 * Register jQuery-based legacy bridges.
 * Must run after jQuery is available on the page.
 */
function registerLegacyJQueryBridge() {
	const $ = window.jQuery;
	if (!$) {
		return;
	}

	// ── $.fn.popup bridge ──────────────────────────────────
	// Legacy code calls: popup.popup('hide')
	// where `popup` is a jQuery object: $('.wlfmc-popup')
	// The modern popup-lite exposes hidePopup(element).
	if (!$.fn.popup) {
		$.fn.popup = function (action) {
			const el = this[0];
			if (!el) return this;
			switch (action) {
				case 'hide':
					hidePopup(el);
					break;
				case 'show':
					showPopup(el);
					break;
				case 'destroy':
					destroyPopup(el);
					break;
			}
			return this;
		};
	}

	// ── $.fn.WLFMC namespace ───────────────────────────────
	// Legacy code calls: $.fn.WLFMC.is_cookie_enabled() etc.
	// We only fill in methods that are missing, so if the
	// old script is also loaded, nothing is overwritten.
	if (!$.fn.WLFMC) {
		$.fn.WLFMC = {};
	}

	if (!$.fn.WLFMC.is_cookie_enabled) {
		$.fn.WLFMC.is_cookie_enabled = function () {
			return validators.isCookieEnabled();
		};
	}

	if (!$.fn.WLFMC.isTrue) {
		$.fn.WLFMC.isTrue = function (value) {
			return validators.isTrue(value);
		};
	}

	if (!$.fn.WLFMC.validateEmail) {
		$.fn.WLFMC.validateEmail = function (email) {
			return validators.isValidEmail(email);
		};
	}

	// Loading indicator: adds the same CSS classes used
	// by the modern conflict-manager visual feedback.
	if (!$.fn.WLFMC.loading) {
		$.fn.WLFMC.loading = function (el) {
			if (!el) return;
			const hasIcon = el.find('i').length > 0;
			el.addClass('wlfmc-action')
				.addClass(hasIcon ? 'wlfmc-loading' : 'wlfmc-loading-alt');
		};
	}

	if (!$.fn.WLFMC.unloading) {
		$.fn.WLFMC.unloading = function (el) {
			if (!el) return;
			el.removeClass('wlfmc-loading wlfmc-loading-alt');
		};
	}

	if (!$.fn.WLFMC.setCookie) {
		$.fn.WLFMC.setCookie = function (name, value, days) {
			validators.setCookie(name, value, days);
		};
	}

	if (!$.fn.WLFMC.getCookie) {
		$.fn.WLFMC.getCookie = function (name) {
			return validators.getCookie(name);
		};
	}
}

// Execute the bridge as soon as jQuery is available.
// system.js is loaded via wp_enqueue_scripts with jQuery
// as a dependency in most setups, but we add a safety net.
if (window.jQuery) {
	registerLegacyJQueryBridge();
} else {
	// jQuery not yet loaded — wait for DOMContentLoaded
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', registerLegacyJQueryBridge);
	} else {
		registerLegacyJQueryBridge();
	}
}

// ═══════════════════════════════════════════════════════════
// MODERN SYSTEM FACTORY
// ═══════════════════════════════════════════════════════════

export function createModernSystem(modules = []) {
	return {
		initialized: false,
		_reinitializing: false,
		_isEnabled: false,
		_premium: false,
		_initialRefreshDone: false,
		_modules: modules.reduce((acc, mod) => {
			acc[mod.name] = mod;
			return acc;
		}, {}),
		async init(options = {}) {
			if (this.initialized) return;
			this._premium = options.premium || false;
			try {
				const userId = window.wlfmc_l10n?.userId || 0;
				const sessionId = window.wlfmc_l10n?.sessionId || '';
				const mergeLists = validators.isTrue(window.wlfmc_l10n?.merge_lists);
				await accountGuard.checkAndPurge();
				stateCache.setConfig({
					userId: userId || '', sessionId: sessionId || '',
					isLoggedIn: userId ? true : false, mergeLists: mergeLists ? true : false
				});
				queueManager.init();
				conflictManager.init();
				await Promise.all(modules.map((mod) => mod.init()));
				await resilientStorage.init();
				await recoverFromPersistence();
				initPersistenceWriter();
				await Promise.all([
					displayStore.recover('wishlist'),
					displayStore.recover('waitlist'),
					displayStore.recover('multiList'),
				]);
				syncManager.init();
				thirdPartyIntegration.init();
				toastr.applyConfig();
				eventManager.registerCoreHandlers();
				uiManager.init({ premium: this._premium });
				this._setupEventListeners();
				this._isEnabled = true;
				this.initialized = true;
				eventBus.emit(EVENTS.SYSTEM_READY, {
					version: '3.0.0',
					premium: this._premium,
				});
				Object.values(this._modules).forEach(mod => {
					if (typeof mod.applyState === 'function') mod.applyState();
				});
				eventBus.emit('system:data-ready');
				const $ = window.jQuery;
				if ($) $(document).trigger('wlfmc_init');
				if (!this._initialRefreshDone) {
					this._initialRefreshDone = true;
					const initialSync = syncManager
						.syncAll({ forceLocal: true })
						.catch(error => {
							console.warn('[ModernSystem] Initial sync failed:', error);
						});
					setInitGate(initialSync);
				} else {
					setInitGate(Promise.resolve());
				}
			} catch (error) {
				console.error('[ModernSystem] Init failed:', error);
			}
		},
		_setupEventListeners() {
			eventBus.on(EVENTS.WISHLIST_ADDED, (data) => {
				if (data.fromOtherTab) return;
				if (data.status === 'success' || data.status === 'already_exists') {
					syncManager.broadcastUpdate({
						type: EVENTS.WISHLIST_ADDED,
						productId: data.productId,
						data,
					});
				}
			});
			eventBus.on(EVENTS.WISHLIST_REMOVED, (data) => {
				if (data.fromOtherTab) return;
				if (data.status === 'success') {
					syncManager.broadcastUpdate({
						type: EVENTS.WISHLIST_REMOVED,
						productId: data.productId,
						data,
					});
				}
			});
			eventBus.on(EVENTS.WAITLIST_ADDED, (data) => {
				if (data.fromOtherTab) return;
				if (data.status === 'success' || data.status === 'updated') {
					syncManager.broadcastUpdate({
						type: EVENTS.WAITLIST_ADDED,
						productId: data.productId,
						data,
					});
				}
			});
			eventBus.on(EVENTS.MULTI_LIST_ADDED, (data) => {
				if (data.fromOtherTab) return;
				if (data.status === 'success') {
					syncManager.broadcastUpdate({
						type: EVENTS.MULTI_LIST_ADDED,
						productId: data.productId,
						data,
					});
					// In merge mode, multi-list additions also affect the
					// wishlist counter. Broadcast a wishlist event so other
					// tabs update their wishlist counters reactively.
					const isMerge = stateCache.getConfig()?.mergeLists === true;
					if (isMerge && data.productId && data.data) {
						// Pass the actual serverResponse (data.data) which contains
						// display info with remove_url and remove_nonce
						syncManager.broadcastUpdate({
							type: EVENTS.WISHLIST_ADDED,
							productId: data.productId,
							data: {
								...data.data,
								mergedFromMultiList: true,
							},
						});
					}
				}
			});
			eventBus.on(EVENTS.MULTI_LIST_REMOVED, (data) => {
				if (data.fromOtherTab) return;
				if (data.status === 'success') {
					syncManager.broadcastUpdate({
						type: EVENTS.MULTI_LIST_REMOVED,
						productId: data.productId,
						data,
					});
					// In merge mode, multi-list removals also affect the
					// wishlist counter. Broadcast a wishlist event so other
					// tabs update their wishlist counters reactively.
					const isMerge = stateCache.getConfig()?.mergeLists === true;
					if (isMerge && data.productId) {
						syncManager.broadcastUpdate({
							type: EVENTS.WISHLIST_REMOVED,
							productId: data.productId,
							data: { ...data, mergedFromMultiList: true },
						});
					}
				}
			});
			eventBus.on(EVENTS.MULTI_LIST_CREATED, (data) => {
				if (data.fromOtherTab) return;
				if (data.status === 'success') {
					syncManager.broadcastUpdate({
						type: EVENTS.MULTI_LIST_CREATED,
						data,
					});
				}
			});
			eventBus.on(EVENTS.MULTI_LIST_DELETED, (data) => {
				if (data.fromOtherTab) return;
				if (data.status === 'success') {
					syncManager.broadcastUpdate({
						type: EVENTS.MULTI_LIST_DELETED,
						data,
					});
					// In merge mode, deleting a list removes all its products
					// from the merged wishlist view. Notify other tabs.
					const isMerge = stateCache.getConfig()?.mergeLists === true;
					if (isMerge) {
						syncManager.broadcastUpdate({
							type: EVENTS.WISHLIST_REMOVED,
							productId: null,
							data: { ...data, mergedListDeleted: true },
						});
					}
				}
			});
			eventBus.on(EVENTS.ACTION_FAILED, (data) => {
				if (data.error) console.warn('[Action Failed]', data);
			});
			eventBus.on(EVENTS.OFFLINE_STATUS_CHANGED, (data) => {
				console.log(
					'[ModernSystem]',
					data.online ? 'Back online' : 'Going offline'
				);
			});
			document.addEventListener('add_to_cart_message', (ev) => {
				const { message, button } = ev.detail || {};
				eventBus.emit('cart:message', { message, button });
			});
			let storageWarningShown = false;
			document.addEventListener('wlfmc:storage-state', (event) => {
				const detail = event.detail || {};
				window.wlfmcStorageMode = detail.mode;
				if (detail.volatile && !storageWarningShown) {
					storageWarningShown = true;
					showInfo(window.wlfmc_l10n?.labels?.storage_volatile_warning);
				}
			});
			window.addEventListener('beforeunload', () => {
				conflictManager.persistPendingOperations();
			});
			window.addEventListener('pageshow', (event) => {
				if (event.persisted) {
					syncManager.syncAll();
				}
			});
		},
		getStatus() {
			const moduleStatus = {};
			Object.keys(this._modules).forEach((name) => {
				const mod = this._modules[name];
				moduleStatus[name] = mod.getStatus ? mod.getStatus() : {};
			});
			return {
				initialized: this.initialized,
				enabled: this._isEnabled,
				premium: this._premium,
				queue: queueManager.getStatus(),
				store: stateCache.getFullState(),
				modules: moduleStatus,
			};
		},
		disable() {
			if (!this._isEnabled) return;
			this._isEnabled = false;
		},
	};
}
